import React, { useState } from 'react';
import { HiOutlineDotsHorizontal, HiOutlinePencilAlt, HiOutlineTrash, HiOutlineBan, HiOutlineCreditCard } from 'react-icons/hi';
import { getImageCacheKey, getImageUrl } from '../../../lib/helpers';
import { getShopBilling, TONE_CLASSES, VENUE_TYPE_LABELS } from '../../../lib/shopBilling';
import { ownersSummary } from '../../../lib/shopOwners';
import ShopBillingBlock from './ShopBillingBlock';

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="shrink-0 text-xs text-gray-500">{label}</span>
      <span className="text-right text-xs text-gray-900">{value}</span>
    </div>
  );
}

/**
 * Tarjeta de tienda — rediseño de Figma (`Club_ShopCard`).
 *
 * Cambios frente a la versión anterior:
 *  - una sola insignia de estado en vez de «Alta» + «Suscripción», que contaban
 *    el mismo paso dos veces
 *  - el bloque de Stripe sale a `ShopBillingBlock`, compartido con la tabla
 *  - `Cancelar suscripción` y `Eliminar` bajan al menú «⋯»: son destructivas y
 *    estaban al mismo nivel que el resto
 *  - fuera el «ID: a3f7c1d2», que no le dice nada a quien lo lee
 */
const ShopCard = ({
  id,
  name,
  address,
  city,
  postalCode,
  venueType,
  planTier,
  activationStatus,
  subscriptionStatus,
  shop_owners,
  date_created,
  handleUpdate,
  handleDelete,
  selectedIds = [],
  onToggleSelect,
  handleManageBilling,
  handleCancelSubscription,
  handleStartBilling,
  handleStartSetup,
  shopImage,
}) => {
  const [imageError, setImageError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const billing = getShopBilling({
    plan_tier: planTier,
    activation_status: activationStatus,
    subscription_status: subscriptionStatus,
  });
  const [selectedPlan, setSelectedPlan] = useState(billing.planTier);

  const imageUrl = getImageUrl(shopImage, { cacheKey: getImageCacheKey('venue', id) });
  const checked = selectedIds.some((shopId) => String(shopId) === String(id));
  const fullAddress = [address, city].filter(Boolean).join(', ') || '—';
  const owners = ownersSummary(shop_owners);

  const runAction = (kind, plan) => {
    if (kind === 'setup') handleStartSetup?.(id, plan);
    else if (kind === 'subscribe') handleStartBilling?.(id, plan);
    else handleManageBilling?.(id);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      {/* Imagen */}
      <div className="relative h-40 shrink-0 overflow-hidden bg-gray-100">
        <label className="absolute left-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded bg-white/95 shadow-sm">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggleSelect?.(id)}
            className="h-4 w-4 accent-emerald-600"
            aria-label={`Seleccionar ${name || 'tienda'}`}
          />
        </label>
        {!imageError && imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-3xl font-semibold text-gray-300">
            {name?.charAt(0)?.toUpperCase() || 'T'}
          </div>
        )}
      </div>

      {/* Cuerpo */}
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-gray-900">{name || 'Sin nombre'}</h3>
            {venueType && (
              <p className="text-xs text-gray-500">{VENUE_TYPE_LABELS[venueType] || venueType}</p>
            )}
          </div>
          <span
            className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${TONE_CLASSES[billing.tone]}`}
          >
            {billing.label}
          </span>
        </div>

        <div className="space-y-1">
          <Row label="Dirección" value={fullAddress} />
          <Row label="Propietarios" value={owners || <span className="italic text-gray-400">Sin asignar</span>} />
          <Row label="Alta" value={fmtDate(date_created)} />
        </div>

        <ShopBillingBlock
          billing={billing}
          selectedPlan={selectedPlan}
          onSelectPlan={setSelectedPlan}
          onAction={runAction}
        />
      </div>

      {/* Acciones */}
      <div className="relative flex items-center gap-2 border-t border-gray-100 p-3">
        <button
          type="button"
          onClick={() => handleUpdate?.(id)}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50"
        >
          <HiOutlinePencilAlt className="text-base" aria-hidden />
          Editar tienda
        </button>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={`Más acciones para ${name || 'la tienda'}`}
          aria-expanded={menuOpen}
          className="rounded-lg border border-transparent p-2 text-gray-500 transition-colors hover:bg-gray-100"
        >
          <HiOutlineDotsHorizontal className="text-lg" aria-hidden />
        </button>

        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} aria-hidden />
            <div className="absolute bottom-full right-3 z-20 mb-1 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
              <button
                type="button"
                onClick={() => { setMenuOpen(false); handleManageBilling?.(id); }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
              >
                <HiOutlineCreditCard className="text-base text-gray-400" aria-hidden />
                Gestionar facturación
              </button>
              {billing.hasSubscription && (
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); handleCancelSubscription?.(id); }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                >
                  <HiOutlineBan className="text-base" aria-hidden />
                  Cancelar suscripción
                </button>
              )}
              <button
                type="button"
                onClick={() => { setMenuOpen(false); handleDelete?.(id); }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
              >
                <HiOutlineTrash className="text-base" aria-hidden />
                Eliminar tienda
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShopCard;
